import type { InputType } from "shared/components/base/BaseInputText.vue";
import { type Props as ConfirmDialogModalProps } from "shared/modals/ConfirmDialogModal.vue";
import ModalService from "shared/modals/ModalService";
import { DialogType } from "shared/modals/types";
import type { Nullable } from "shared/types";

export interface DialogServiceOptions extends ConfirmDialogModalProps {
  dialogMaxWidth?: string;
  inputType?: InputType;
  inputValue?: string;
}

export type DialogServiceFunction = (options: DialogServiceOptions) => {
  hide(): void;
  onCancel(fn: Function): ThisType<DialogServiceFunction>;
  onDismiss(fn: Function): ThisType<DialogServiceFunction>;
  onOk(fn: Function): ThisType<DialogServiceFunction>;
};

export function DialogService(options: DialogServiceOptions = {}) {
  let cancelFn: Nullable<Function> = null,
    dismissFn: Nullable<Function> = null,
    okFn: Nullable<Function> = null;

  const API = {
    hide() {
      ModalService.close("ConfirmDialogModal");
    },
    onCancel(fn: Function) {
      cancelFn = fn;

      return this;
    },
    onDismiss(fn: Function) {
      dismissFn = fn;

      return this;
    },
    onOk(fn: Function) {
      okFn = fn;

      return this;
    },
  };

  const transformForModal: (
    options: DialogServiceOptions
  ) => ConfirmDialogModalProps = (opt) => {
    const { dialogMaxWidth, inputType, inputValue, ...rest } = opt;

    return rest;
  };

  ModalService.open("ConfirmDialogModal", {
    events: {
      cancel: () => {
        if (cancelFn instanceof Function) {
          cancelFn();
        }
      },
      close: () => {
        if (dismissFn instanceof Function) {
          dismissFn();
        }
      },
      confirm: (value: string | boolean) => {
        if (okFn instanceof Function) {
          okFn(value);
        }
      },
    },
    props: {
      dialogStyle: {
        maxWidth: options.dialogMaxWidth || null,
      },
      promptInput: {
        props: { type: options.inputType || "text" },
        value: options.inputValue || "",
      },
      ...transformForModal(options),
    },
  });

  return API;
}

DialogService.alert = function alert(options: DialogServiceOptions = {}) {
  return DialogService({
    ...options,
    type: DialogType.ALERT,
  });
};

DialogService.confirm = function confirm(options: DialogServiceOptions = {}) {
  return DialogService({
    ...options,
    type: DialogType.CONFIRM,
  });
};

DialogService.prompt = function prompt(options: DialogServiceOptions = {}) {
  return DialogService({
    ...options,
    type: DialogType.PROMPT,
  });
};

export default DialogService;
